/**
 * BookingIndicator Component
 *
 * A small colored pill/badge that displays a truncated resource name
 * with a background color based on booking status.
 *
 * Validates: Requirements 2.4, 7.2
 * - Requirement 2.4: Each booking indicator SHALL be color-coded by status
 *   (confirmed=blue, pending=yellow, human_supervision=indigo, cancelled=gray).
 * - Requirement 7.2: Each booking indicator SHALL display the resource name.
 */

import { tokens } from '../../../design-system/tokens';
import type { CalendarViewBooking } from '../calendarView.types';
import { airbnbStayKind, airbnbStayShortLabel } from '../calendarView.utils';
import { BookingOriginIcon, bookingOriginTitle } from './BookingOriginIcon';

// ============================================
// TYPES
// ============================================

export interface BookingIndicatorProps {
  /** Name of the resource to display (truncated with ellipsis) */
  resourceName: string;
  /** Booking status — determines the pill color */
  status: CalendarViewBooking['status'];
  bookingOrigin?: CalendarViewBooking['bookingOrigin'];
  otaChannel?: CalendarViewBooking['otaChannel'];
  conversationChannel?: CalendarViewBooking['conversationChannel'];
  customerName?: string;
  createdBy?: string;
  alsoBlockedOnAirbnb?: boolean;
}

// ============================================
// STATUS COLOR MAP
// ============================================

/**
 * Maps booking status to background and text colors using design system tokens.
 *
 * - confirmed → blue (info)
 * - pending → yellow (warning)
 * - human_supervision → indigo (primary)
 * - completed (hist.) → gris neutro
 * - cancelled → gray (neutral)
 */
const STATUS_COLORS: Record<
  CalendarViewBooking['status'],
  { background: string; text: string }
> = {
  confirmed: {
    background: tokens.colors.info.light,
    text: tokens.colors.info.dark,
  },
  pending_deposit: {
    background: tokens.colors.warning.light,
    text: tokens.colors.warning.dark,
  },
  human_supervision: {
    background: tokens.colors.primary[100],
    text: tokens.colors.primary[700],
  },
  completed: {
    background: tokens.colors.neutral[200],
    text: tokens.colors.neutral[600],
  },
  cancelled: {
    background: tokens.colors.neutral[200],
    text: tokens.colors.neutral[600],
  },
  auto_cancelled: {
    background: tokens.colors.neutral[200],
    text: tokens.colors.neutral[500],
  },
};

// ============================================
// HELPER
// ============================================

/** Returns the color pair for a given booking status */
export function getStatusColors(status: CalendarViewBooking['status']) {
  return STATUS_COLORS[status];
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * BookingIndicator — A small colored pill showing the resource name.
 *
 * Used inside DayCell to represent a booking on a given day.
 * The pill color reflects the booking status.
 *
 * @example
 * ```tsx
 * <BookingIndicator resourceName="Cabaña Norte" status="confirmed" />
 * ```
 */
export function BookingIndicator({
  resourceName,
  status,
  bookingOrigin,
  otaChannel,
  conversationChannel,
  customerName,
  createdBy,
  alsoBlockedOnAirbnb,
}: BookingIndicatorProps) {
  const colors = STATUS_COLORS[status];
  const stayKind = airbnbStayKind({ customerName, bookingOrigin, otaChannel, createdBy });
  const stayLabel = airbnbStayShortLabel({ customerName, bookingOrigin, otaChannel, createdBy });
  const origin = bookingOriginTitle({ bookingOrigin, otaChannel, conversationChannel });
  const title = `${stayLabel ?? customerName ?? resourceName} — ${resourceName} — ${origin}${
    alsoBlockedOnAirbnb ? ' — También bloqueado en Airbnb' : ''
  }`;
  const fontWeight =
    stayKind === 'reserved'
      ? tokens.typography.fontWeight.extrabold
      : stayKind === 'block'
        ? tokens.typography.fontWeight.normal
        : tokens.typography.fontWeight.medium;

  return (
    <div
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: `${tokens.spacing[0]} ${tokens.spacing[1]}`,
        borderRadius: tokens.borderRadius.sm,
        backgroundColor: colors.background,
        color: colors.text,
        fontSize: tokens.typography.fontSize.xs,
        fontWeight,
        lineHeight: tokens.typography.lineHeight.tight,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      <BookingOriginIcon
        bookingOrigin={bookingOrigin}
        otaChannel={otaChannel}
        conversationChannel={conversationChannel}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight }}>{resourceName}</span>
    </div>
  );
}

export default BookingIndicator;
